import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  DownloadBulkDataIn,
  DownloadLoadProfileDataIn,
  LoadProfileCommandEvent,
  PageInfo,
  PermissionsEnum,
  QueryScheduledDataReadDataIn,
  QueryScheduledDataReadDataOut,
  SortingType,
} from '../../../api/api-reference';
import { generateFileNameAppendix } from '../../../helper/download-filename';
import { AuthService } from '../../../services/auth.service';
import { FileService } from '../../../services/file.service';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { ClipboardService } from '../../../services/clipboard.service';
import { processDateRange } from '../../../helper/date-utils';
import { ScheduledDataFilterComponent, ScheduledDataFilterOutput } from '../../core/components/table/filter-panel/filters/scheduled-data-filter/scheduled-data-filter.component';
import { IsNewDirective } from '../../../directives/is-new.directive';

@Component({
  selector: 'app-scheduled-instantaneous',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
    FormsModule,
    DatepickerComponent,
    PaginationComponent,
    SearchComponent,
    ScheduledDataFilterComponent,
    IsNewDirective,
  ],
  providers: [],
  templateUrl: './scheduled-instantaneous.component.html',
  styleUrl: './scheduled-instantaneous.component.scss',
})
export class ScheduledInstantaneousComponent {
  data: QueryScheduledDataReadDataOut[] = [];
  public PermisssionEnum = PermissionsEnum;
  public PermissionsEnum = PermissionsEnum;
  private selection: Map<string, QueryScheduledDataReadDataOut> = new Map();
  get isHeaderChecked(): boolean {
    return this.data.length > 0 && this.data.every(obj => this.selection.has(obj.meterId));
  }
  get selectedRows(): string[] { return Array.from(this.selection.keys()); }
  readonly dayInMs = 24 * 60 * 60 * 1000;
  isTableShowing = false;
  isDatepickerShown = false;
  isDatepickerOpen = false;
  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public numberOfDevices: number = 0;
  public lastPageSize: number = 1;
  public pageSize: number = 10;
  public pageNumber: number = 1;

  selectedFilters: ScheduledDataFilterOutput = {
    selectedAssetTypes: [],
    areas: [],
    regions: [],
  };

  requestData = new QueryScheduledDataReadDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    startDate: undefined,
    endDate: undefined,
    search: undefined,
    sorting: SortingType.Descending,
    assetTypes: [],
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private router: Router,
    public authService: AuthService,
    private fileService: FileService,
    public clipboardService: ClipboardService,
    private translate: TranslateService,
  ) {
    this.reloadTable(1);
  }

  isAllSelected() {
    return this.data.every((obj) => this.isRowSelected(obj.meterId));
  }
  isRowSelected(meterId: string) {
    return this.selection.has(meterId);
  }
  toggleAllSelected(event: Event) {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      let limited = false;
      for (const obj of this.data) {
        if (this.selection.size >= 100) { limited = true; break; }
        if (!this.selection.has(obj.meterId)) this.selection.set(obj.meterId, obj);
      }
      if (limited) {
        this.toastr.warning('You can select a maximum of 100 entities for bulk actions.');
      }
    } else {
      this.data.forEach((obj) => this.selection.delete(obj.meterId));
    }
  }
  toggleRowSelection(row: QueryScheduledDataReadDataOut, event: Event) {
    event.stopPropagation();
    if (this.selection.has(row.meterId)) {
      this.selection.delete(row.meterId);
    } else {
      if (this.selection.size >= 100) {
        event.preventDefault();
        this.toastr.warning('You can select a maximum of 100 entities for bulk actions.');
        return;
      }
      this.selection.set(row.meterId, row);
    }
  }
  clearSelection() {
    this.selection.clear();
  }
  downloadSelected() {
    const now = new Date();
    const startDate = this.requestData.startDate ?? new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = this.requestData.endDate ?? new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    const data = new DownloadBulkDataIn({
      meters:
        this.selectedRows && this.selectedRows.length > 0
          ? this.selectedRows
          : this.data.map(x => x.meterId),
      startDate,
      endDate,
    });
    this.client.downloadBulkInstantaneousCombined(data).subscribe({
      next: (response) => {
        if (response?.includes(' ')) {
          this.toastr.warning(response);
        } else {
          this.client.downloadBulkFile(response).subscribe({
            next: (file) => {
              const extension = file.data.type === 'application/zip' ? 'zip' : 'xlsx';
              const fileNameAppendix = generateFileNameAppendix(extension);
              this.fileService.downloadFile(file.data, file.fileName || `BulkInstantaneous${fileNameAppendix}`);
            },
            error: (err) => this.toastr.error(err),
          });
        }
      },
      error: (err) => this.toastr.error(err),
    });
  }

  onDataReceived(event: ScheduledDataFilterOutput) {
    this.selectedFilters = event;
    this.requestData.assetTypes = event.selectedAssetTypes;
    this.requestData.areas = event.areas;
    this.requestData.regions = event.regions;
    this.clearSelection();
    this.reloadTable(1);
  }

  setInputDate(event: string | number | Date): Date {
    const inputDate = new Date(event);
    return inputDate;
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.clearSelection();
    this.reloadTable(1);
  }

  download(meterId: string) {
    const fileNameApendix = generateFileNameAppendix();
    const data = new DownloadLoadProfileDataIn({
      meterId: meterId,
      startDate: this.requestData.startDate,
      endDate: this.requestData.endDate
    })
    this.client.downloadProcessedInstantaneousObjectFile(data).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `InstantaneousObject${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.queryLastScheduledDevices(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data!;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
   const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.clearSelection();
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.clearSelection();
    this.reloadTable();
  }

  filterDatesInTable() {
    this.client.queryLastScheduledDevices(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data!;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  rowClick(meterId: string) {
    this.router.navigate(['/scheduled/instantaneous-object-details'], {
      queryParams: {
        meterId,
        startDate: this.requestData.startDate?.toISOString(),
        endDate: this.requestData.endDate?.toISOString(),
        selectedDate: this.selectedDate,
      },
    });
  }
}
